import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Avatar,
  Body1,
  Body1Strong,
  Button,
  Caption1,
  Card,
  ProgressBar,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
  createTableColumn,
  makeStyles,
  tokens,
  useTableFeatures,
} from '@fluentui/react-components';
import { AddFilled, DocumentSearchRegular } from '@fluentui/react-icons';

import { usePersistedColumnSizing } from '@shared/hooks';
import { useItemTableStyles } from '@modules/items';

import { TChangeV2 } from '../../models';
import { formatDate, getChangeReference, getChangeScopeSummary, getDayOffset, isBlocked, isOverdue } from '../../utils';
import { getChangeV2WorkspacePath } from '../../helpers';
import { CriticalityBadge } from '../shared/CriticalityBadge';
import { StatusBadge } from '../shared/StatusBadge';
import { EmptyState } from '../shared/EmptyState';

const useStyles = makeStyles({
  muted: { color: tokens.colorNeutralForeground3 },
  overdue: {
    color: tokens.colorStatusDangerForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  changeCell: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  changeLink: {
    display: 'block',
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
    textDecorationLine: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ':hover': { textDecorationLine: 'underline' },
  },
  managerCell: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  progressCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  tooltipContent: { display: 'flex', flexDirection: 'column', gap: '2px' },
});

type TColumnId = 'change' | 'status' | 'manager' | 'criticality' | 'progress' | 'targetDate';

const COLUMN_IDS: TColumnId[] = ['change', 'status', 'manager', 'criticality', 'progress', 'targetDate'];

const HEADER_LABEL: Record<TColumnId, string> = {
  change: 'Change',
  status: 'Status',
  manager: 'Change manager',
  criticality: 'Criticality',
  progress: 'Progress',
  targetDate: 'Target date',
};

const CHANGE_V2_TABLE_ID = 'change-v2-dashboard-table';

const columnSizingParams = {
  autoFitColumns: false,
  columnSizingOptions: {
    change: { defaultWidth: 340, minWidth: 240 },
    status: { defaultWidth: 150, minWidth: 120 },
    manager: { defaultWidth: 200, minWidth: 150 },
    criticality: { defaultWidth: 120, minWidth: 96 },
    progress: { defaultWidth: 150, minWidth: 110 },
    targetDate: { defaultWidth: 150, minWidth: 120 },
  },
};

type TProps = {
  changes: TChangeV2[];
  hasFilters?: boolean;
  onCreate?: () => void;
  onClearFilters?: () => void;
};

export const ChangesTable = ({ changes, hasFilters, onCreate, onClearFilters }: TProps) => {
  const classes = useStyles();
  const tableClasses = useItemTableStyles();

  const columns = useMemo(() => COLUMN_IDS.map((columnId) => createTableColumn<TChangeV2>({ columnId })), []);
  const { columnSizing_unstable, getRows } = useTableFeatures(
    { columns, items: changes, getRowId: (item) => item.id },
    [usePersistedColumnSizing(CHANGE_V2_TABLE_ID, columnSizingParams)]
  );
  const rows = getRows();

  if (changes.length === 0) {
    return (
      <Card className={tableClasses.card}>
        {hasFilters ? (
          <EmptyState
            icon={<DocumentSearchRegular />}
            title="No changes match the selected filters"
            message="Adjust or clear the filters to see more changes."
            action={
              onClearFilters ? (
                <Button appearance="secondary" onClick={onClearFilters}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        ) : (
          <EmptyState
            icon={<DocumentSearchRegular />}
            title="No changes yet"
            message="Create the first change to start tracking it here."
            action={
              onCreate ? (
                <Button appearance="primary" icon={<AddFilled />} onClick={onCreate}>
                  Create change
                </Button>
              ) : undefined
            }
          />
        )}
      </Card>
    );
  }

  return (
    <Card className={tableClasses.card}>
      <Table className={tableClasses.table} aria-label="Change register" {...columnSizing_unstable.getTableProps()}>
        <TableHeader>
          <TableRow>
            {COLUMN_IDS.map((columnId) => (
              <TableHeaderCell key={columnId} {...columnSizing_unstable.getTableHeaderCellProps(columnId)}>
                <Body1Strong>{HEADER_LABEL[columnId]}</Body1Strong>
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody className={tableClasses.tableBody}>
          {rows.map(({ item }) => {
            const overdue = isOverdue(item);
            const offset = getDayOffset(item.targetDate);
            const blocked = isBlocked(item);
            return (
              <TableRow key={item.id}>
                <TableCell {...columnSizing_unstable.getTableCellProps('change')}>
                  {/* Next action + change id surface on hover, so they no longer need their own columns. */}
                  <Tooltip
                    relationship="description"
                    withArrow
                    positioning="above-start"
                    content={
                      <div className={classes.tooltipContent}>
                        <Body1Strong>{item.title || 'Untitled change'}</Body1Strong>
                        <Caption1 className={classes.muted}>{getChangeReference(item)}</Caption1>
                        <Body1>Next action: {item.nextAction ?? 'None'}</Body1>
                        {blocked ? <Caption1 className={classes.overdue}>Blocked</Caption1> : null}
                      </div>
                    }
                  >
                    <div className={classes.changeCell}>
                      <Link to={getChangeV2WorkspacePath(item.id)} className={classes.changeLink}>
                        {item.title || 'Untitled change'}
                      </Link>
                      <Caption1 className="truncate block">
                        <span className={classes.muted}>{getChangeScopeSummary(item)}</span>
                      </Caption1>
                    </div>
                  </Tooltip>
                </TableCell>
                <TableCell {...columnSizing_unstable.getTableCellProps('status')}>
                  <StatusBadge status={item.status} size="medium" />
                </TableCell>
                <TableCell {...columnSizing_unstable.getTableCellProps('manager')}>
                  {item.changeManager ? (
                    <div className={classes.managerCell}>
                      <Avatar name={item.changeManager} color="colorful" size={24} />
                      <Body1 className="truncate">{item.changeManager}</Body1>
                    </div>
                  ) : (
                    <Body1 className={classes.muted}>Unassigned</Body1>
                  )}
                </TableCell>
                <TableCell {...columnSizing_unstable.getTableCellProps('criticality')}>
                  {item.criticality ? <CriticalityBadge criticality={item.criticality} /> : <Body1>—</Body1>}
                </TableCell>
                <TableCell {...columnSizing_unstable.getTableCellProps('progress')}>
                  <div className={classes.progressCell}>
                    <ProgressBar value={item.progress / 100} thickness="large" />
                    <Caption1 className={classes.muted}>{item.progress}%</Caption1>
                  </div>
                </TableCell>
                <TableCell {...columnSizing_unstable.getTableCellProps('targetDate')}>
                  <div className="flex flex-col">
                    <Body1 className={overdue ? classes.overdue : undefined}>{formatDate(item.targetDate)}</Body1>
                    {item.targetDate && offset !== undefined ? (
                      <Caption1 className={overdue ? classes.overdue : classes.muted}>
                        {overdue ? `${Math.abs(offset)} days overdue` : `Due in ${offset} days`}
                      </Caption1>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
};
